import React from "react";
import { Button } from "../ui/button";
import PaginatedTable, { PaginatedTableColumn } from "../ui/paginated-table";
import { Progress } from "../ui/progress";
import { HistoryIcon } from "lucide-react";
import { cn } from "../../lib/utils";
import moment from "moment";

interface Review {
  id: number;
  date_created: number;
  date_completed?: number;
  num_reviews: number;
  num_corrections: number;
  status: string;
}

interface ReviewsTableProps {
  reviews: Review[];
  loading: boolean;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onRetryReview: (reviewId: number) => void;
  reviewProgress: Record<string, any>;
  emptyTitle?: string;
  emptyDescription?: string;
}

const ReviewsTable: React.FC<ReviewsTableProps> = ({
  reviews,
  loading,
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onRetryReview,
  reviewProgress,
  emptyTitle = "No reviews found",
  emptyDescription = "Start an AI review to see results here."
}) => {
  const formatDate = (timestamp: number) => {
    if (!timestamp || timestamp <= 0) return "Never";
    const date = moment(timestamp);
    return date.format("MMM D, YYYY");
  };

  const formatTime = (timestamp: number) => {
    if (!timestamp || timestamp <= 0) return "";
    const date = moment(timestamp);
    return date.format("h:mm A");
  };

  const getStatusTagColor = (status: string): string => {
    switch (status?.toUpperCase()?.trim()) {
      case "CREATED":
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400";
      case "COMPLETED":
      case "COMPLETE":
        return "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400";
      case "FAILED":
      case "ERROR":
        return "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400";
      case "PENDING":
      case "PROCESSING":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400";
    }
  };

  const renderStatus = (review: Review) => {
    const progressData = reviewProgress[review.id.toString()];
    const currentStatus = progressData?.status || review.status;

    if (currentStatus !== "RUNNING" && currentStatus !== "PROCESSING") {
      const completed_count =
        progressData?.corrected_count +
        progressData?.good_count +
        progressData?.error_count;
      const tooltipContent =
        (currentStatus === "ERROR" ||
          currentStatus === "COMPLETE" ||
          currentStatus === "COMPLETED") &&
        progressData
          ? `${completed_count}/${progressData.total_count} reviews completed (${progressData.corrected_count} corrected, ${progressData.good_count} good, ${progressData.error_count} errors)`
          : undefined;

      return (
        <div title={tooltipContent}>
          <span className={cn(
            "inline-flex items-center px-2 py-1 rounded text-xs font-medium",
            getStatusTagColor(currentStatus)
          )}>
            {currentStatus.toUpperCase()}
          </span>
        </div>
      );
    }

    if (!progressData) {
      return (
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
          <span className="text-sm text-muted-foreground">Processing...</span>
        </div>
      );
    }

    const { total_count, corrected_count, good_count, error_count } = progressData;
    const completed_count = corrected_count + good_count + error_count;
    const percent = total_count > 0 ? (completed_count / total_count) * 100 : 0;

    return (
      <div className="w-24">
        <Progress
          value={Number(percent.toFixed(1))}
          className="h-2"
        />
        <div className="text-xs text-muted-foreground mt-1">
          {`${completed_count}/${total_count}`}
        </div>
      </div>
    );
  };

  const canRetry = (status: string) => {
    return ["FAILED", "COMPLETED", "SCHEDULED", "CANCELLED"].includes(status.toUpperCase());
  };

  const hasProcessingReview = reviews.some((review) => review.status === "PROCESSING");

  const columns: PaginatedTableColumn<Review>[] = [
    {
      key: "date_created",
      header: "Date Started",
      width: "w-[20%]",
      render: (review) => (
        <div className="space-y-1">
          <div className="text-sm font-gc-reg text-foreground">
            {formatDate(review.date_created)}
          </div>
          <div className="text-xs text-muted-foreground">
            {formatTime(review.date_created)}
          </div>
        </div>
      )
    },
    {
      key: "date_completed",
      header: "Date Completed",
      width: "w-[20%]",
      render: (review) => (
        review.date_completed ? (
          <div className="space-y-1">
            <div className="text-sm font-gc-reg text-foreground">
              {formatDate(review.date_completed)}
            </div>
            <div className="text-xs text-muted-foreground">
              {formatTime(review.date_completed)}
            </div>
          </div>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        )
      )
    },
    {
      key: "num_reviews",
      header: "# Reviews",
      width: "w-[15%]",
      render: (review) => (
        <span className="text-sm font-gc-med text-foreground">
          {review.num_reviews}
        </span>
      )
    },
    {
      key: "num_corrections",
      header: "# Corrections",
      width: "w-[15%]",
      render: (review) => (
        <span className="text-sm font-gc-med text-foreground">
          {review.num_corrections}
        </span>
      )
    },
    {
      key: "status",
      header: "Status",
      width: "w-[20%]",
      render: renderStatus
    },
    {
      key: "id",
      header: "Actions",
      width: "w-[10%]",
      render: (review) => (
        <Button
          variant="ghost"
          size="sm"
          className="gap-1"
          disabled={
            review.status === "PROCESSING" ||
            review.status === "RUNNING" ||
            hasProcessingReview ||
            !canRetry(review.status)
          }
          onClick={() => onRetryReview(review.id)}
          title={
            review.status === "PROCESSING" || review.status === "RUNNING"
              ? "Cannot retry while this review is still processing"
              : hasProcessingReview
              ? "Cannot retry while another review is processing"
              : review.status === "ERROR"
              ? "Click to retry this failed review"
              : ""
          }
        >
          <HistoryIcon className="w-3 h-3" />
          Retry
        </Button>
      )
    }
  ];

  return (
    <PaginatedTable<Review>
      data={reviews}
      columns={columns}
      loading={loading}
      currentPage={currentPage}
      onPageChange={onPageChange}
      pageSize={itemsPerPage}
      totalItems={totalItems}
      serverSidePagination={true}
      emptyTitle={emptyTitle}
      emptyDescription={emptyDescription}
      rowClassName="hover:bg-accent/20 transition-colors"
    />
  );
};

export default ReviewsTable; 